import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../../config';
import type {
  TimedRunInvalidReason,
  TimedRunLeaderboardPage,
  TimedRunPublicEntry,
  TimedRunRulesetHistoryResponse,
} from '../../../shared/types/TimedRun';
import {
  formatTimedRunElapsed,
  timedRunElapsedMs,
  useTimedRunClientState,
} from '../../services/TimedRunClientService';
import { formatCallingName } from '../theme';
import { TomeButton, TomeChapter, TomeFolio } from '../primitives/tome';
import './SpeedrunRecords.css';

const PAGE_SIZE = 25;

const INVALID_REASON_LABELS = {
  wrong_experience: 'Wrong world experience',
  unsupported_flask: 'Unsupported runner flask',
  production_identity_ineligible: 'Production identity was not eligible',
  practice_flask_in_production: 'Practice flask used in production',
  required_quest_already_completed: 'Required quest was already completed',
  character_already_attempted: 'Character already attempted this ruleset',
  debug_or_admin_shortcut: 'Debug or admin shortcut detected',
  ruleset_changed: 'Ruleset changed during the run',
  active_run_restarted: 'Active run was restarted',
  required_quest_chain_incomplete: 'Required quest chain was incomplete',
  character_persistence_failed: 'Character save was not confirmed',
  admin_invalidated: 'Invalidated after review',
} satisfies Record<TimedRunInvalidReason, string>;

function invalidReasonLabel(reason: TimedRunInvalidReason | undefined): string {
  return reason ? INVALID_REASON_LABELS[reason] : 'Reason unavailable';
}

function entryDetail(entry: TimedRunPublicEntry): string {
  if (entry.status === 'invalidated') {
    return `Invalidated · ${invalidReasonLabel(entry.invalidReason)}`;
  }
  if (entry.mode === 'practice') {
    return entry.identitySource === 'guest'
      ? 'Guest practice · excluded from ranked records'
      : 'Practice · excluded from ranked records';
  }
  return 'Ranked · production eligible';
}

interface RulesetChoice {
  rulesetId: string;
  releaseVersion: string;
  label: string;
  completionCount?: number;
}

async function fetchJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export const SpeedrunRecordsContent: React.FC = () => {
  const playerRun = useTimedRunClientState();
  const [history, setHistory] = useState<TimedRunRulesetHistoryResponse | null>(null);
  const [selectedRuleset, setSelectedRuleset] = useState('');
  const [page, setPage] = useState(1);
  const [board, setBoard] = useState<TimedRunLeaderboardPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [includePractice, setIncludePractice] = useState(false);
  const [includeInvalidated, setIncludeInvalidated] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    fetchJson<TimedRunRulesetHistoryResponse>(`${API_URL}/speedruns/rulesets`, controller.signal)
      .then(result => {
        setHistory(result);
        setSelectedRuleset(current => current || result.currentRuleset?.rulesetId || result.rulesets[0]?.rulesetId || '');
      })
      .catch(reason => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setError(reason instanceof Error ? reason.message : 'Could not load ruleset history.');
        setLoading(false);
      });
    return () => controller.abort();
  }, [refreshKey]);

  useEffect(() => {
    if (!selectedRuleset) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const query = new URLSearchParams({
      ruleset: selectedRuleset,
      page: String(page),
      limit: String(PAGE_SIZE),
      includePractice: String(includePractice),
      includeInvalidated: String(includeInvalidated),
    });
    fetchJson<TimedRunLeaderboardPage>(
      `${API_URL}/speedruns/leaderboard?${query.toString()}`,
      controller.signal,
    )
      .then(result => setBoard(result))
      .catch(reason => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setError(reason instanceof Error ? reason.message : 'Could not load the leaderboard.');
        setBoard(null);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [selectedRuleset, page, refreshKey, includePractice, includeInvalidated]);

  const rulesets = useMemo<RulesetChoice[]>(() => {
    if (!history) return [];
    const choices: RulesetChoice[] = [];
    if (history.currentRuleset) {
      choices.push({
        rulesetId: history.currentRuleset.rulesetId,
        releaseVersion: history.currentRuleset.releaseVersion,
        label: `${history.currentRuleset.displayName} — current`,
      });
    }
    for (const row of history.rulesets) {
      if (choices.some(choice => choice.rulesetId === row.rulesetId)) {
        const current = choices.find(choice => choice.rulesetId === row.rulesetId)!;
        current.completionCount = row.completionCount;
        continue;
      }
      choices.push({
        rulesetId: row.rulesetId,
        releaseVersion: row.releaseVersion,
        label: `Historic ruleset ${row.rulesetId}`,
        completionCount: row.completionCount,
      });
    }
    return choices;
  }, [history]);

  const ownRun = playerRun.message?.run;
  const totalPages = Math.max(1, Math.ceil((board?.total ?? 0) / PAGE_SIZE));

  return <section className="records-tome" aria-label="Coil records">
    <header className="records-tome__head">
      <div>
        <h3>Relight the Coil</h3>
        <p>Full main story · timer ends on final quest turn-in</p>
      </div>
      <TomeButton tone="secondary" cut="clean" onClick={() => setRefreshKey(value => value + 1)}>Refresh</TomeButton>
    </header>

    {ownRun && <TomeFolio className="records-tome__folio" aria-label="Your run" shapeSeed={ownRun.mode}>
      <div className="records-tome__run">
        <div>
          <span className="records-tome__kicker">Your run</span>
          <p>{ownRun.mode === 'ranked' ? 'Ranked' : 'Practice'} · {ownRun.status.replace(/_/g, ' ')}
            {ownRun.rank != null ? ` · #${ownRun.rank}${ownRun.boardTotal != null ? ` of ${ownRun.boardTotal}` : ''}` : ''}</p>
          {ownRun.runnerDisplayName && <p className="records-tome__twitch">Twitch · {ownRun.runnerDisplayName}</p>}
          {ownRun.status === 'invalidated' && <p className="records-tome__invalid">{invalidReasonLabel(ownRun.invalidReason)}</p>}
        </div>
        <strong>{formatTimedRunElapsed(timedRunElapsedMs(playerRun))}</strong>
      </div>
    </TomeFolio>}

    <label className="records-tome__ruleset">Version &amp; ruleset
      <select aria-label="Version and ruleset" value={selectedRuleset}
        onChange={event => { setSelectedRuleset(event.target.value); setPage(1); }}>
        {rulesets.length === 0 && <option value="">No published rulesets</option>}
        {rulesets.map(choice => <option key={choice.rulesetId} value={choice.rulesetId}>
          {choice.label} · v{choice.releaseVersion}{choice.completionCount !== undefined ? ` · ${choice.completionCount} finishes` : ''}
        </option>)}
      </select>
    </label>

    <div className="records-tome__filters" role="group" aria-label="Record visibility">
      <label><input type="checkbox" checked={includePractice}
        onChange={event => { setIncludePractice(event.target.checked); setPage(1); }} />Include guest / practice runs</label>
      <label><input type="checkbox" checked={includeInvalidated}
        onChange={event => { setIncludeInvalidated(event.target.checked); setPage(1); }} />Include invalidated runs</label>
      <em>Ranked production finishes only by default.</em>
    </div>

    {error && <div className="records-tome__error" role="alert">{error}</div>}

    {!error && <TomeChapter className="records-tome__board" title="Finishes" count={board?.total}>
      <table>
        <thead><tr><th>#</th><th>Runner</th><th>Calling</th><th data-align="end">Time</th><th data-align="end">Version</th></tr></thead>
        <tbody>
          {board?.entries.map(entry => <tr key={entry.runId} data-podium={entry.rank != null && entry.rank <= 3 || undefined}>
            <td className="records-tome__rank">{entry.rank == null ? '—' : entry.mode === 'practice' ? `P${entry.rank}` : entry.rank}</td>
            <td>
              <div className="records-tome__hero">{entry.heroName}</div>
              {entry.runnerDisplayName && <div className="records-tome__twitch">Twitch · {entry.runnerDisplayName}</div>}
              <div className="records-tome__detail" data-status={entry.status}>{entryDetail(entry)}</div>
            </td>
            <td>{formatCallingName(entry.callingId)}</td>
            <td data-align="end" className="records-tome__time">{entry.elapsedMs == null ? '—' : formatTimedRunElapsed(entry.elapsedMs)}</td>
            <td data-align="end" className="records-tome__version">v{entry.releaseVersion}</td>
          </tr>)}
          {!loading && (board?.entries.length ?? 0) === 0 && <tr><td colSpan={5} className="records-tome__empty">
            {includePractice || includeInvalidated
              ? 'No runs match these visibility filters.'
              : 'No production-eligible ranked finishes yet. Include test runs to inspect practice or invalidated attempts.'}
          </td></tr>}
          {loading && <tr><td colSpan={5} className="records-tome__empty">Loading records…</td></tr>}
        </tbody>
      </table>
    </TomeChapter>}

    <footer className="records-tome__pager">
      <span>{board?.total ?? 0} {includePractice || includeInvalidated ? 'matching' : 'ranked'} runs</span>
      <span>
        <TomeButton tone="secondary" cut="clean" disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))}>Previous</TomeButton>
        Page {page} / {totalPages}
        <TomeButton tone="secondary" cut="clean" disabled={page >= totalPages} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>Next</TomeButton>
      </span>
    </footer>
  </section>;
};
